import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';

export type SelectOption = {
  value: string;
  label: string;
  dotColor?: string;
};

export function CustomSelect({
  value,
  options,
  onChange,
  icon,
  placeholder,
  disabled = false
}: {
  value: string;
  options: SelectOption[];
  onChange: (val: string) => void;
  icon?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [present,setPresent]=useState(false);
  useEffect(()=>{if(open){setPresent(true);return;}const timer=setTimeout(()=>setPresent(false),140);return()=>clearTimeout(timer);},[open]);
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<React.CSSProperties>({});

  // The list is portalled to <body> with fixed coordinates so scroll containers and dialogs never clip it;
  // it opens upwards when there is not enough room below the trigger.
  useLayoutEffect(() => {
    if (!present) return;
    const update = () => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      const want = Math.min(280, options.length * 40 + 12);
      const below = window.innerHeight - r.bottom - 12, above = r.top - 12;
      const up = below < want && above > below;
      setPlace({
        position: 'fixed', left: r.left, width: r.width, zIndex: 10050,
        maxHeight: Math.max(120, Math.min(280, up ? above : below)), overflowY: 'auto',
        ...(up ? { bottom: window.innerHeight - r.top + 6, top: 'auto' } : { top: r.bottom + 6, bottom: 'auto' })
      });
    };
    update();
    const onScroll = (e: Event) => { if (!listRef.current?.contains(e.target as Node)) setOpen(false); };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', onScroll, true);
    return () => { window.removeEventListener('resize', update); window.removeEventListener('scroll', onScroll, true); };
  }, [present, options.length]);

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node) && !listRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  const current = options.find(o => o.value === value) || (placeholder ? undefined : options[0]);

  return (
    <div ref={ref} className={'custom-device-select ' + (open ? 'is-open ' : '') + (disabled ? 'is-disabled' : '')}>
      <button
        type="button"
        className="custom-device-select-trigger"
        disabled={disabled}
        onClick={() => !disabled && setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="listbox"
        onKeyDown={e => { if (e.key === 'Escape') setOpen(false); if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); } }}
      >
        <span className="custom-device-select-label">
          {icon}
          {current?.dotColor && (
            <i
              style={{
                width: 9,
                height: 9,
                borderRadius: '50%',
                background: current.dotColor,
                display: 'inline-block',
                flexShrink: 0
              }}
            />
          )}
          <span>{current?.label || placeholder || value}</span>
        </span>
        <ChevronDown size={16} className="custom-device-chevron" />
      </button>
      {present && createPortal(
        <div ref={listRef} style={place} className={"custom-device-dropdown is-portal " + (!open ? "is-closing" : "")} role="listbox" onKeyDown={e=>{if(e.key==='Escape'){setOpen(false);ref.current?.querySelector('button')?.focus();}}}>
          {options.map(opt => {
            const active = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={active}
                className={'custom-device-option ' + (active ? 'is-selected' : '')}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  {opt.dotColor && (
                    <i
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        background: opt.dotColor,
                        display: 'inline-block',
                        flexShrink: 0
                      }}
                    />
                  )}
                  {opt.label}
                </span>
                {active && <Check size={15} />}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}

export function HeadphonesOffIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 14.36-7.24" />
      <path d="M21 14h-3a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-7a8.96 8.96 0 0 0-.67-3.42" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  );
}

export function SixDigitCodeInput({
  name = 'code',
  value,
  onChange,
  autoFocus = true,
  disabled = false
}: {
  name?: string;
  value?: string;
  onChange?: (code: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  const [digits, setDigits] = useState<string[]>(() => {
    const clean = (value || '').replace(/\D/g, '').slice(0, 6);
    return Array.from({ length: 6 }, (_, i) => clean[i] || '');
  });
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (value !== undefined) {
      const clean = value.replace(/\D/g, '').slice(0, 6);
      setDigits(Array.from({ length: 6 }, (_, i) => clean[i] || ''));
    }
  }, [value]);

  function updateDigits(next: string[]) {
    setDigits(next);
    onChange?.(next.join(''));
  }

  function handleChange(idx: number, raw: string) {
    const clean = raw.replace(/\D/g, '');
    if (!clean) {
      const next = [...digits];
      next[idx] = '';
      updateDigits(next);
      return;
    }
    if (clean.length > 1) {
      const next = [...digits];
      for (let i = 0; i < clean.length && idx + i < 6; i++) {
        next[idx + i] = clean[i];
      }
      updateDigits(next);
      const focusIdx = Math.min(5, idx + clean.length);
      inputsRef.current[focusIdx]?.focus();
      return;
    }
    const next = [...digits];
    next[idx] = clean[0];
    updateDigits(next);
    if (idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
  }

  function handleKeyDown(idx: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      const next = [...digits];
      next[idx - 1] = '';
      updateDigits(next);
      inputsRef.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowRight' && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted) {
      e.preventDefault();
      const next = Array.from({ length: 6 }, (_, i) => pasted[i] || '');
      updateDigits(next);
      inputsRef.current[Math.min(5, pasted.length)]?.focus();
    }
  }

  return (
    <div className="six-digit-otp-wrap six-digit-code-row" onPaste={handlePaste}>
      <input type="hidden" name={name} value={digits.join('')} />
      {digits.map((d, i) => (
        <input
          key={i}
          ref={el => {
            inputsRef.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={6}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          className={'otp-digit-box six-digit-box ' + (d ? 'is-filled' : '')}
          value={d}
          placeholder="·"
          aria-label={`Digit ${i + 1}`}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
        />
      ))}
    </div>
  );
}
